import React, { useEffect } from 'react';
import { AlertTriangle, Info, CheckCircle2, XCircle, X } from 'lucide-react';

interface ConfirmDialogProps {
  isOpen: boolean;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  type?: 'danger' | 'info' | 'warning';
  onConfirm: () => void;
  onClose: () => void;
}

export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  isOpen,
  title,
  message,
  confirmText = 'Bestätigen',
  cancelText = 'Abbrechen',
  type = 'info',
  onConfirm,
  onClose,
}) => {
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const colorMap = {
    danger: {
      bg: 'bg-rose-500/10 text-rose-600 dark:text-rose-400',
      btn: 'bg-rose-600 hover:bg-rose-700 shadow-rose-600/10',
      icon: AlertTriangle,
    },
    warning: {
      bg: 'bg-amber-500/10 text-amber-600 dark:text-amber-400',
      btn: 'bg-amber-600 hover:bg-amber-700 shadow-amber-600/10',
      icon: AlertTriangle,
    },
    info: {
      bg: 'bg-indigo-500/10 text-indigo-650 dark:text-indigo-400',
      btn: 'bg-indigo-600 hover:bg-indigo-700 shadow-indigo-600/10',
      icon: Info,
    },
  };

  const config = colorMap[type] || colorMap.info;
  const Icon = config.icon;

  return (
    <div
      role="alertdialog"
      aria-modal="true"
      aria-label={title}
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-md animate-in fade-in duration-200"
    >
      <div className="bg-white dark:bg-slate-950 w-full max-w-sm rounded-3xl shadow-2xl border border-slate-200 dark:border-white/5 p-7 space-y-6 animate-in zoom-in-95 duration-200 text-center relative overflow-hidden">
        <div className="absolute top-4 right-4">
          <button
            type="button"
            aria-label="Schließen"
            onClick={onClose}
            className="p-1.5 text-slate-400 hover:text-slate-600 dark:hover:text-white rounded-lg transition-colors"
          >
            <X size={16} />
          </button>
        </div>
        <div className={`mx-auto w-14 h-14 rounded-2xl flex items-center justify-center ${config.bg}`}>
          <Icon size={26} className="stroke-[2.5]" />
        </div>
        <div className="space-y-2">
          <h3 className="text-base font-black text-slate-800 dark:text-white uppercase tracking-wider">{title}</h3>
          <p className="text-xs font-bold leading-relaxed text-slate-500 dark:text-slate-400">{message}</p>
        </div>
        <div className="flex gap-3 pt-2">
          <button type="button" onClick={onClose} className="flex-1 px-4 py-3 text-[11px] font-black uppercase tracking-widest text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl transition-all">
            {cancelText}
          </button>
          <button type="button" onClick={() => { onConfirm(); onClose(); }} className={`flex-1 px-4 py-3 text-white text-[11px] font-black uppercase tracking-widest rounded-xl shadow-lg transition-all active:scale-95 ${config.btn}`}>
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
};

interface AlertDialogProps {
  isOpen: boolean;
  title: string;
  message: string;
  confirmText?: string;
  type?: 'success' | 'error' | 'info';
  onClose: () => void;
}

export const AlertDialog: React.FC<AlertDialogProps> = ({
  isOpen,
  title,
  message,
  confirmText = 'OK',
  type = 'info',
  onClose,
}) => {
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const colorMap = {
    success: {
      bg: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400',
      btn: 'bg-emerald-600 hover:bg-emerald-700 shadow-emerald-600/10',
      icon: CheckCircle2,
    },
    error: {
      bg: 'bg-rose-500/10 text-rose-600 dark:text-rose-400',
      btn: 'bg-rose-600 hover:bg-rose-700 shadow-rose-600/10',
      icon: XCircle,
    },
    info: {
      bg: 'bg-indigo-500/10 text-indigo-650 dark:text-indigo-400',
      btn: 'bg-indigo-600 hover:bg-indigo-700 shadow-indigo-600/10',
      icon: Info,
    },
  };

  const config = colorMap[type] || colorMap.info;
  const Icon = config.icon;

  return (
    <div
      role="alertdialog"
      aria-modal="true"
      aria-label={title}
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-md animate-in fade-in duration-200"
    >
      <div className="bg-white dark:bg-slate-950 w-full max-w-sm rounded-3xl shadow-2xl border border-slate-200 dark:border-white/5 p-7 space-y-6 animate-in zoom-in-95 duration-200 text-center relative overflow-hidden">
        <div className="absolute top-4 right-4">
          <button
            type="button"
            aria-label="Schließen"
            onClick={onClose}
            className="p-1.5 text-slate-400 hover:text-slate-600 dark:hover:text-white rounded-lg transition-colors"
          >
            <X size={16} />
          </button>
        </div>
        <div className={`mx-auto w-14 h-14 rounded-2xl flex items-center justify-center ${config.bg}`}>
          <Icon size={26} className="stroke-[2.5]" />
        </div>
        <div className="space-y-2">
          <h3 className="text-base font-black text-slate-800 dark:text-white uppercase tracking-wider">{title}</h3>
          <p className="text-xs font-bold leading-relaxed text-slate-500 dark:text-slate-400">{message}</p>
        </div>
        <div className="pt-2">
          <button type="button" onClick={onClose} className={`w-full py-3.5 text-white text-[11px] font-black uppercase tracking-widest rounded-xl shadow-lg transition-all active:scale-95 ${config.btn}`}>
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
};
